import React, { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormText,
  Input,
  InputGroup,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import { z } from "zod";
import { requestJson } from "../../../helpers/api";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { obtenerEmpresaActivaId } from "../../../utils/empresa";
import { jsPDF } from "jspdf";
import RecetasListadoTabla from "./components/RecetasListadoTabla";

type ModalInfoType = "success" | "error" | "info";

export type RecetaResumen = {
  id: number;
  codigo: string;
  nombre: string;
  etiquetaResumen?: string;
  resumenLineas?: string[];
  estado: string;
  vigenteDesde?: string | null;
  vigenteHasta?: string | null;
  empresaId?: number | null;
  actualizadoEn?: string | null;
  creadoEn?: string | null;
  creadoPor?: number | null;
  creadoPorNombre?: string | null;
  actualizadoPor?: number | null;
  actualizadoPorNombre?: string | null;
};

export type AccionRecetaBoton = {
  key: string;
  label: string;
  icon: string;
  color: string;
  flatClass?: string;
  softClass?: string;
  type?: never;
  variant?: never;
};

export type AccionRecetaSwitch = {
  key: string;
  label: string;
  type: "switch";
  icon: string;
};

export type AccionReceta = AccionRecetaBoton | AccionRecetaSwitch;

type RecetaItemForm = {
  articuloId: string;
  cantidad: string;
  unidad?: string;
  descripcion?: string;
  articuloLabel?: string;
  esReceta?: boolean;
};

type RecetaDetalleItem = {
  id?: number;
  articuloId?: number | null;
  cantidad?: number | null;
  unidadMedidaId?: number | null;
  tipoItem?: string | null;
  subrecetaId?: number | null;
};

type RecetaDetalle = RecetaResumen & {
  descripcion?: string | null;
  unidadMedidaId?: number | null;
  items?: RecetaDetalleItem[];
};

type IngredienteValidado = {
  articuloId: number;
  cantidad: number;
};

type StockArticulo = {
  id: number;
  descripcion: string;
  sku?: string | null;
  unidad_medida?: string | null;
  unidad_medida_id?: number | null;
  es_receta?: boolean;
  tipoItem?: string | null;
  puede_comprar?: boolean;
  puede_vender?: boolean;
};

type StockArticuloApi = {
  id: string | number;
  descripcion: string;
  sku?: string | null;
  unidad_medida?: string | null;
  unidad_medida_id?: string | number | null;
  tipoitem?: string | null;
  tipoItem?: string | null;
  es_receta?: boolean;
  puede_comprar?: boolean;
  puede_vender?: boolean;
};

const parseBoolean = (valor: unknown): boolean => {
  if (typeof valor === "boolean") return valor;
  if (typeof valor === "number") return valor !== 0;
  if (typeof valor === "string") {
    const limpio = valor.trim().toLowerCase();
    return limpio === "true" || limpio === "t" || limpio === "1" || limpio === "si";
  }
  return false;
};

const vigenciaSchema = z
  .object({
    vigenteDesde: z.string().optional(),
    vigenteHasta: z.string().optional(),
  })
  .refine(
    (data) =>
      !data.vigenteDesde ||
      !data.vigenteHasta ||
      new Date(data.vigenteHasta) >= new Date(data.vigenteDesde),
    {
      message: "La fecha 'Vigente hasta' no puede ser anterior a 'Vigente desde'.",
      path: ["vigenteHasta"],
    },
  );

const MODAL_TABLE_MAX_HEIGHT = 360;
const STICKY_TH_STYLE: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "var(--bs-light, #f5f6fb)",
  color: "var(--bs-dark, #101234)",
  fontWeight: 600,
};

const crearItemVacio = (): RecetaItemForm => ({
  articuloId: "",
  cantidad: "1",
  esReceta: false,
});

const normalizarArticuloId = (valor: number | string | null | undefined): number => {
  if (typeof valor === "number") {
    return Number.isFinite(valor) ? valor : 0;
  }
  if (typeof valor === "string") {
    const parsed = Number(valor);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const crearArticuloPlaceholder = (id: number | string | null | undefined, descripcion: string): StockArticulo => ({
  id: normalizarArticuloId(id),
  descripcion,
  sku: "",
  unidad_medida: "",
  unidad_medida_id: undefined,
  es_receta: true,
});

const ARTICULOS_DATALIST_ID = "articulos-receta";

type FormSnapshot = {
  selectedArticulo: StockArticulo | null;
  vigenciaDesde: string;
  vigenciaHasta: string;
  cantidadResultado: string;
  items: RecetaItemForm[];
};

const RecetasPage = () => {
  const [recetas, setRecetas] = useState<RecetaResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [vigenciaDesde, setVigenciaDesde] = useState("");
  const [vigenciaHasta, setVigenciaHasta] = useState("");
  const [cantidadResultado, setCantidadResultado] = useState("1");
  const [cantidadError, setCantidadError] = useState<string | null>(null);
  const [vigenciaError, setVigenciaError] = useState<string | null>(null);
  const [items, setItems] = useState<RecetaItemForm[]>([crearItemVacio()]);
  const [articulos, setArticulos] = useState<StockArticulo[]>([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [selectedArticulo, setSelectedArticulo] = useState<StockArticulo | null>(null);
  const [articuloBusqueda, setArticuloBusqueda] = useState("");
  const [seleccionandoItemIndex, setSeleccionandoItemIndex] = useState<number | null>(null);
  const seleccionandoItemRef = useRef<number | null>(null);
  const formularioSnapshotRef = useRef<FormSnapshot | null>(null);
  const [modalInfo, setModalInfo] = useState<{
    tipo: ModalInfoType;
    titulo: string;
    mensaje: string;
  } | null>(null);
  const cerrarModalResultado = () => setModalInfo(null);
  const mostrarModalInfo = (tipo: ModalInfoType, titulo: string, mensaje: string) => {
    setModalInfo({ tipo, titulo, mensaje });
  };
  const [accionProcesando, setAccionProcesando] = useState<{ id: number; key: AccionRecetaSwitch['key'] } | null>(null);
  const [busquedaReceta, setBusquedaReceta] = useState("");
  const [modalEdicionAbierto, setModalEdicionAbierto] = useState(false);
  const [recetaEditandoId, setRecetaEditandoId] = useState<number | null>(null);
  const [recetaEditandoNombre, setRecetaEditandoNombre] = useState<string>("");
  const [cargandoDetalleEdicion, setCargandoDetalleEdicion] = useState(false);
  const [savingEdicion, setSavingEdicion] = useState(false);
  const [modalVerRecetaAbierto, setModalVerRecetaAbierto] = useState(false);
  const [detalleRecetaVista, setDetalleRecetaVista] = useState<RecetaDetalle | null>(null);
  const [cargandoDetalleVista, setCargandoDetalleVista] = useState(false);
  const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
  const [recetaHistorialBase, setRecetaHistorialBase] = useState<RecetaResumen | null>(null);
  const [historialRecetas, setHistorialRecetas] = useState<RecetaResumen[]>([]);
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState<string>("");
  const [filtroVigenteDesde, setFiltroVigenteDesde] = useState<string>("");
  const [filtroVigenteHasta, setFiltroVigenteHasta] = useState<string>("");
  const [filtroNombreReceta, setFiltroNombreReceta] = useState<string>("");
  const [modalImportacionAbierto, setModalImportacionAbierto] = useState(false);
  const [archivoImportacion, setArchivoImportacion] = useState<File | null>(null);
  const [importando, setImportando] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const handleApiError = useCallback((error: unknown, fallback: string) => {
    const mensajeError =
      error instanceof Error ? error.message : typeof error === "string" ? error : fallback;
    return mensajeError;
  }, []);

  const cargarRecetas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const datos = await requestJson<RecetaResumen[]>("/api/produccion/recetas");
      setRecetas(Array.isArray(datos) ? datos : []);
    } catch (err) {
      setError(handleApiError(err, "No se pudieron cargar las recetas."));
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  const empresaId = useMemo(() => obtenerEmpresaActivaId() ?? 1, []);

  const cargarArticulos = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        empresaId: String(empresaId),
      });
      const datos = await requestJson<StockArticuloApi[]>(
        `/api/stock/catalogos/articulos?${params.toString()}`,
      );
  if (Array.isArray(datos)) {
    setArticulos(
      datos.map((articulo) => ({
        id: Number(articulo.id),
        descripcion: articulo.descripcion,
        sku: articulo.sku ?? null,
        unidad_medida: articulo.unidad_medida ?? null,
        unidad_medida_id:
          typeof articulo.unidad_medida_id === "string"
            ? Number(articulo.unidad_medida_id)
            : articulo.unidad_medida_id ?? undefined,
        tipoItem:
          articulo.tipoitem ??
          articulo.tipoItem ??
          undefined,
        es_receta:
          typeof articulo.es_receta !== "undefined"
            ? parseBoolean(articulo.es_receta)
            : (articulo.tipoitem ?? articulo.tipoItem)?.toLowerCase() === "receta",
        puede_comprar: parseBoolean(articulo.puede_comprar),
        puede_vender: parseBoolean(articulo.puede_vender),
      })),
        );
      } else {
        setArticulos([]);
      }
    } catch (err) {
      console.error("No se pudieron cargar los artículos de stock", err);
    }
  }, [empresaId]);

  useEffect(() => {
    void cargarRecetas();
    void cargarArticulos();
  }, [cargarRecetas, cargarArticulos]);

  const validarItems = (lista: RecetaItemForm[]): IngredienteValidado[] =>
    lista
      .map((item) => {
        const articuloId = Number(item.articuloId);
        const cantidad = Number(item.cantidad);
        if (!Number.isFinite(articuloId) || articuloId <= 0) {
          return null;
        }
        if (!Number.isFinite(cantidad) || cantidad <= 0) {
          return null;
        }
        return { articuloId, cantidad };
      })
      .filter((item): item is IngredienteValidado => Boolean(item));

  const ingredientesValidos = useMemo(() => validarItems(items), [items]);
  const puedeEnviar =
    Boolean(selectedArticulo) &&
    ingredientesValidos.length > 0 &&
    Boolean(vigenciaDesde) &&
    Boolean(vigenciaHasta) &&
    !vigenciaError &&
    !cantidadError;
  const textoRetornoFormulario = "Volverás al formulario para seguir registrando recetas.";

  const crearSnapshotFormulario = useCallback((): FormSnapshot => {
    return {
      selectedArticulo,
      vigenciaDesde,
      vigenciaHasta,
      cantidadResultado,
      items: items.map((item) => ({ ...item })),
    };
  }, [selectedArticulo, vigenciaDesde, vigenciaHasta, cantidadResultado, items]);

  const restaurarFormulario = useCallback((snapshot?: FormSnapshot | null) => {
    const base =
      snapshot ??
      {
        selectedArticulo: null,
        vigenciaDesde: "",
        vigenciaHasta: "",
        cantidadResultado: "1",
        items: [crearItemVacio()],
      };
    setSelectedArticulo(base.selectedArticulo);
    setVigenciaDesde(base.vigenciaDesde);
    setVigenciaHasta(base.vigenciaHasta);
    setCantidadResultado(base.cantidadResultado);
    setItems(base.items.map((item) => ({ ...item })));
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setMensaje(null);
    setError(null);
    try {
      if (!selectedArticulo) {
        const texto = `Seleccioná la receta principal (artículo). ${textoRetornoFormulario}`;
        setError("Seleccioná la receta principal (artículo).");
        mostrarModalInfo("error", "No se pudo guardar la receta", texto);
        return;
      }
      if (vigenciaError) {
        const texto = `${vigenciaError} ${textoRetornoFormulario}`;
        setError(vigenciaError);
        mostrarModalInfo("error", "No se pudo guardar la receta", texto);
        return;
      }
      if (cantidadError) {
        const texto = `${cantidadError} ${textoRetornoFormulario}`;
        setError(cantidadError);
        mostrarModalInfo("error", "No se pudo guardar la receta", texto);
        return;
      }
      const payload = {
        codigo: `${selectedArticulo.id}`,
        nombre: selectedArticulo.descripcion,
        unidadMedidaId: selectedArticulo.unidad_medida_id ?? undefined,
        empresaId: undefined,
        vigenteDesde: vigenciaDesde || undefined,
        vigenteHasta: vigenciaHasta || undefined,
        items: validarItems(items).map((item) => ({
          articuloId: item.articuloId,
          cantidad: item.cantidad,
          tipoItem: "materia_prima",
        })),
      };
      if (!payload.codigo || !payload.nombre || payload.items.length === 0) {
        const texto = `Completa código, nombre e ingredientes válidos. ${textoRetornoFormulario}`;
        setError("Completa código, nombre e ingredientes válidos.");
        mostrarModalInfo("error", "No se pudo guardar la receta", texto);
        return;
      }
      await requestJson("/api/produccion/recetas", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setMensaje("Receta creada correctamente.");
      mostrarModalInfo(
        "success",
        "Receta guardada",
        `La receta se guardó correctamente. ${textoRetornoFormulario}`,
      );
      restaurarFormulario();
      await cargarRecetas();
    } catch (err) {
      const mensajeError = handleApiError(err, "No se pudo crear la receta.");
      setError(mensajeError);
      mostrarModalInfo("error", "Error al guardar la receta", `${mensajeError}. ${textoRetornoFormulario}`);
    } finally {
      setSaving(false);
    }
  };

  const handleItemChange = (index: number, field: keyof RecetaItemForm, value: string) =>
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );

  const formatArticuloLabel = (articulo: StockArticulo) => {
    return `ID ${articulo.id} - ${articulo.descripcion}`;
  };

  const esIngredienteDuplicado = (articuloId: number, indexActual?: number) =>
    items.some(
      (item, idx) =>
        idx !== indexActual &&
        Number(item.articuloId) > 0 &&
        Number(item.articuloId) === articuloId,
    );

  const aplicarArticuloEnItem = (index: number, articulo: StockArticulo) => {
    const id = Number(articulo.id);
    if (esIngredienteDuplicado(id, index)) {
      setError("Ya agregaste este artículo como ingrediente.");
      return false;
    }
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              articuloId: `${articulo.id}`,
              unidad:
                articulo.unidad_medida?.trim() ||
                (articulo.unidad_medida_id != null ? String(articulo.unidad_medida_id) : ""),
              descripcion: articulo.descripcion ?? item.descripcion ?? "",
              articuloLabel: formatArticuloLabel(articulo),
              esReceta:
                typeof articulo.es_receta !== "undefined"
                  ? parseBoolean(articulo.es_receta)
                  : (articulo.tipoItem ?? "").toLowerCase() === "receta",
            }
          : item,
      ),
    );
    setError(null);
    return true;
  };

  const seleccionarArticulo = (index: number, valor: string) => {
    const idTexto = valor.split(" - ")[0];
    const articuloId = Number(idTexto);
    const articulo = articulos.find((item) => item.id === articuloId);
    if (articulo) {
      if (!aplicarArticuloEnItem(index, articulo)) {
        return;
      }
      return;
    }
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              articuloId: valor,
              articuloLabel: valor,
            }
          : item,
      ),
    );
  };

  const abrirModal = (index: number | null) => {
    seleccionandoItemRef.current = index;
    setSeleccionandoItemIndex(index);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    seleccionandoItemRef.current = null;
    setSeleccionandoItemIndex(null);
  };

  const handleSeleccionDesdeModal = (articulo: StockArticulo) => {
    const contextoIndex = seleccionandoItemRef.current;
  if (contextoIndex === null) {
    setSelectedArticulo(articulo);
  } else {
      if (selectedArticulo && selectedArticulo.id === articulo.id) {
        setError("No podés usar la misma receta como ingrediente.");
        return;
      }
      if (!aplicarArticuloEnItem(contextoIndex, articulo)) {
        return;
      }
  }
  cerrarModal();
};

  const totalCantidad = useMemo(
    () =>
      items.reduce(
        (acum, item) =>
          acum + (Number(item.cantidad) > 0 ? Number(item.cantidad) : 0),
        0,
      ),
    [items],
  );

  const recetasActivas = useMemo(
    () => recetas.filter((receta) => receta.estado === "activo"),
    [recetas],
  );
  const recetasActivasCodigos = useMemo(
    () => new Set(recetasActivas.map((receta) => receta.codigo)),
    [recetasActivas],
  );
  const recetasVisibles = useMemo(() => recetas, [recetas]);
  const obtenerDetalleReceta = useCallback(
    (recetaId: number) => requestJson<RecetaDetalle>(`/api/produccion/recetas/${recetaId}`),
    [],
  );
  const mostrarAccionPendiente = (accion: string, receta: RecetaResumen) =>
    mostrarModalInfo(
      "info",
      accion,
      `La acción "${accion}" para la receta "${receta.nombre}" todavía no está implementada.`,
    );
  const iniciarEdicionReceta = useCallback(
    async (receta: RecetaResumen) => {
      formularioSnapshotRef.current = crearSnapshotFormulario();
      setError(null);
      setMensaje(null);
      setModalEdicionAbierto(true);
      setCargandoDetalleEdicion(true);
      try {
        const detalle = await obtenerDetalleReceta(receta.id);
        const articuloPrincipal =
          articulos.find((articulo) => String(articulo.id) === detalle.codigo) ??
          crearArticuloPlaceholder(detalle.codigo ?? detalle.id, detalle.nombre);
        setSelectedArticulo(articuloPrincipal);
        setVigenciaDesde(detalle.vigenteDesde ?? "");
        setVigenciaHasta(detalle.vigenteHasta ?? "");
        setCantidadResultado("1");
        const nuevosItems =
          detalle.items && detalle.items.length > 0
            ? detalle.items.map((item) => {
                const articuloItem =
                  item.articuloId != null ? articulos.find((articulo) => articulo.id === item.articuloId) : undefined;
                return {
                  articuloId: item.articuloId ? String(item.articuloId) : "",
                  cantidad: item.cantidad ? String(item.cantidad) : "1",
                  unidad:
                    articuloItem?.unidad_medida ??
                    (item.unidadMedidaId != null ? String(item.unidadMedidaId) : ""),
                  descripcion: articuloItem?.descripcion ?? undefined,
                  articuloLabel: articuloItem
                    ? formatArticuloLabel(articuloItem)
                    : item.articuloId
                    ? `ID ${item.articuloId}`
                    : undefined,
                  esReceta: item.tipoItem === "receta",
                };
              })
            : [crearItemVacio()];
        setItems(nuevosItems);
        setRecetaEditandoId(detalle.id);
        setRecetaEditandoNombre(detalle.nombre);
      } catch (err) {
        setModalEdicionAbierto(false);
        restaurarFormulario(formularioSnapshotRef.current);
        formularioSnapshotRef.current = null;
        setError(handleApiError(err, "No se pudo cargar la receta para editar."));
      } finally {
        setCargandoDetalleEdicion(false);
      }
    },
    [
      articulos,
      crearSnapshotFormulario,
      formatArticuloLabel,
      handleApiError,
      obtenerDetalleReceta,
      restaurarFormulario,
    ],
  );
  const cerrarModalEdicion = useCallback(() => {
    restaurarFormulario(formularioSnapshotRef.current);
    formularioSnapshotRef.current = null;
    setModalEdicionAbierto(false);
    setRecetaEditandoId(null);
    setRecetaEditandoNombre("");
  }, [restaurarFormulario]);
  const handleSubmitEdicion = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!recetaEditandoId) {
        setError("No se pudo determinar la receta a editar.");
        return;
      }
      if (!selectedArticulo) {
        setError("No se encontró el artículo principal de la receta.");
        return;
      }
      setSavingEdicion(true);
      setMensaje(null);
      setError(null);
      try {
        const payload = {
          nombre: selectedArticulo.descripcion,
          unidadMedidaId: selectedArticulo.unidad_medida_id ?? null,
          vigenteDesde: vigenciaDesde || null,
          vigenteHasta: vigenciaHasta || null,
          items: validarItems(items).map((item) => ({
            articuloId: item.articuloId,
            cantidad: item.cantidad,
            tipoItem: "materia_prima",
          })),
        };
        if (!payload.items || payload.items.length === 0) {
          setError("Agregá al menos un ingrediente válido.");
          return;
        }
        await requestJson(`/api/produccion/recetas/${recetaEditandoId}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        mostrarModalInfo(
          "success",
          "Receta actualizada",
          `La receta "${recetaEditandoNombre || selectedArticulo.descripcion}" se actualizó correctamente.`,
        );
        await cargarRecetas();
        cerrarModalEdicion();
      } catch (err) {
        setError(handleApiError(err, "No se pudo actualizar la receta."));
      } finally {
        setSavingEdicion(false);
      }
    },
    [
      cargarRecetas,
      cerrarModalEdicion,
      handleApiError,
      items,
      mostrarModalInfo,
      recetaEditandoId,
      recetaEditandoNombre,
      selectedArticulo,
      validarItems,
      vigenciaDesde,
      vigenciaHasta,
    ],
  );
  const abrirModalVerReceta = useCallback(
    async (receta: RecetaResumen) => {
      setModalVerRecetaAbierto(true);
      setDetalleRecetaVista(null);
      setCargandoDetalleVista(true);
      try {
        const detalle = await obtenerDetalleReceta(receta.id);
        setDetalleRecetaVista(detalle);
      } catch (err) {
        setModalVerRecetaAbierto(false);
        setError(handleApiError(err, "No se pudo cargar la receta."));
      } finally {
        setCargandoDetalleVista(false);
      }
    },
    [handleApiError, obtenerDetalleReceta],
  );
  const cerrarModalVerReceta = () => {
    setModalVerRecetaAbierto(false);
    setDetalleRecetaVista(null);
  };
  const abrirModalImportacion = () => {
    setImportError(null);
    setModalImportacionAbierto(true);
  };
  const cerrarModalImportacion = () => {
    setModalImportacionAbierto(false);
    setArchivoImportacion(null);
    setImportError(null);
  };
  const manejarArchivoImportacion = (event: ChangeEvent<HTMLInputElement>) => {
    const archivo = event.target.files?.[0] ?? null;
    setArchivoImportacion(archivo);
    setImportError(null);
  };
  const handleImportarRecetas = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!archivoImportacion) {
      setImportError("Seleccioná un archivo en formato CSV o XLSX.");
      return;
    }
    setImportando(true);
    setImportError(null);
    try {
      const formData = new FormData();
      formData.append("archivo", archivoImportacion);
      const response = await fetch("/api/produccion/recetas/importar", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const texto = await response.text();
        throw new Error(texto || "No se pudo importar las recetas.");
      }
      mostrarModalInfo(
        "success",
        "Importación en proceso",
        "Estamos procesando las recetas importadas. Volvé a consultar el listado en unos instantes.",
      );
      await cargarRecetas();
      cerrarModalImportacion();
    } catch (err) {
      setImportError(handleApiError(err, "No se pudo importar las recetas."));
    } finally {
      setImportando(false);
    }
  };
  const abrirModalHistorial = useCallback(
    (receta: RecetaResumen) => {
      const versiones = recetas
        .filter((item) => item.codigo === receta.codigo)
        .sort((a, b) => {
          const fechaA = a.vigenteDesde ?? a.actualizadoEn ?? "";
          const fechaB = b.vigenteDesde ?? b.actualizadoEn ?? "";
          return fechaA < fechaB ? 1 : fechaA > fechaB ? -1 : 0;
        });
      setRecetaHistorialBase(receta);
      setHistorialRecetas(versiones);
      setModalHistorialAbierto(true);
    },
    [recetas],
  );
  const cerrarModalHistorial = () => {
    setModalHistorialAbierto(false);
    setRecetaHistorialBase(null);
    setHistorialRecetas([]);
  };
  const [confirmacionEstado, setConfirmacionEstado] = useState<{
    receta: RecetaResumen;
    nuevoEstado: "activo" | "inactivo" | "anulado";
    accionKey: AccionRecetaSwitch["key"];
    descripcion: string;
  } | null>(null);
  const esSwitch = (accion: AccionReceta): accion is AccionRecetaSwitch => accion.type === "switch";
  const accionesReceta: AccionReceta[] = [
    {
      key: "anular",
      label: "Anular",
      type: "switch",
      icon: "ri-close-circle-line",
    },
    {
      key: "inactivar",
      label: "Inactivar",
      type: "switch",
      icon: "ri-toggle-line",
    },
    {
      key: "editar",
      label: "Editar",
      icon: "ri-edit-2-line",
      color: "success",
      flatClass: "btn-success",
    },
    {
      key: "ver",
      label: "Ver",
      icon: "ri-eye-line",
      color: "info",
      softClass: "btn-soft-info",
    },
    {
      key: "historial",
      label: "Ver historial",
      icon: "ri-time-line",
      color: "secondary",
    },
  ];
  const cambiarEstadoReceta = useCallback(
    async (
      recetaId: number,
      estado: "activo" | "inactivo" | "anulado",
      mensajeExito: string,
      accionKey: AccionRecetaSwitch['key'],
    ) => {
      setAccionProcesando({ id: recetaId, key: accionKey });
      setMensaje(null);
      setError(null);
      try {
        await requestJson(`/api/produccion/recetas/${recetaId}/estado`, {
          method: "PATCH",
          body: JSON.stringify({ estado }),
        });
        setMensaje(mensajeExito);
        await cargarRecetas();
      } catch (err) {
        setError(handleApiError(err, "No se pudo actualizar el estado de la receta."));
      } finally {
        setAccionProcesando(null);
      }
    },
    [cargarRecetas, handleApiError],
  );
  const confirmarCambioEstado = useCallback(() => {
    if (!confirmacionEstado) return;
    const { receta, nuevoEstado, accionKey } = confirmacionEstado;
    const mensaje =
      nuevoEstado === "activo"
        ? "Receta activada correctamente."
        : nuevoEstado === "inactivo"
        ? "Receta marcada como inactiva."
        : "Receta anulada correctamente.";
    void cambiarEstadoReceta(receta.id, nuevoEstado, mensaje, accionKey);
    setConfirmacionEstado(null);
  }, [cambiarEstadoReceta, confirmacionEstado]);

  const handleToggleActivo = useCallback(
    (receta: RecetaResumen) => {
      const nuevoEstado = receta.estado === "activo" ? "inactivo" : "activo";
      const descripcion =
        nuevoEstado === "activo"
          ? "¿Querés activar nuevamente esta receta?"
          : "¿Querés marcar la receta como inactiva? No podrá usarse hasta volver a activarla.";
      setConfirmacionEstado({
        receta,
        nuevoEstado,
        accionKey: "inactivar",
        descripcion,
      });
    },
    [],
  );
  const handleAnularReceta = useCallback(
    (receta: RecetaResumen) => {
      if (receta.estado !== "inactivo") {
        setError("La receta debe estar inactiva antes de poder anularla.");
        return;
      }
      setConfirmacionEstado({
        receta,
        nuevoEstado: "anulado",
        accionKey: "anular",
        descripcion:
          "Esta acción anula la receta y no podrá revertirse. ¿Confirmás que querés continuar?",
      });
    },
    [],
  );

  const articulosFiltrados = useMemo(() => {
    const termino = articuloBusqueda.trim().toLowerCase();
    const mostrarSoloRecetas = seleccionandoItemIndex === null;
    return articulos
      .filter((articulo) => {
        const tipo = (articulo.tipoItem ?? "").toLowerCase();
        const esReceta = articulo.es_receta ?? tipo === "receta";
        if (mostrarSoloRecetas) {
          const codigoArticulo = String(articulo.id);
          return esReceta && recetasActivasCodigos.has(codigoArticulo);
        }
        return true;
      })
      .filter((articulo) => {
        if (seleccionandoItemIndex !== null && selectedArticulo?.id === articulo.id) {
          return false;
        }
        if (!termino) return true;
        const texto = `${articulo.id} ${articulo.sku ?? ""} ${articulo.descripcion}`.toLowerCase();
        return texto.includes(termino);
      });
  }, [articuloBusqueda, articulos, seleccionandoItemIndex, selectedArticulo]);

  const agregarItem = () =>
    setItems((prev) => [
      ...prev,
      crearItemVacio(),
    ]);
  const removerItem = (index: number) =>
    setItems((prev) => prev.filter((_, idx) => idx !== index));

  const recetasOrdenadas = useMemo(
    () => [...recetasVisibles].sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [recetasVisibles],
  );
  const nombresRecetasDisponibles = useMemo(() => {
    const conjunto = new Set<string>();
    recetasOrdenadas.forEach((receta) => {
      const nombre = receta.nombre?.trim();
      if (nombre) conjunto.add(nombre);
    });
    return Array.from(conjunto).sort((a, b) => a.localeCompare(b));
  }, [recetasOrdenadas]);
  const estadoBadgeColor = {
    activo: "success",
    inactivo: "warning",
    anulado: "danger",
  } as const;

  const recetasFiltradas = useMemo(() => {
    const termino = busquedaReceta.trim().toLowerCase();
    const nombreFiltro = filtroNombreReceta.trim().toLowerCase();
    return recetasOrdenadas.filter((receta) => {
      const camposBusqueda = [
        receta.id.toString(),
        receta.codigo,
        receta.nombre,
        receta.estado,
        receta.empresaId?.toString() ?? "",
        receta.creadoPorNombre ?? "",
        receta.actualizadoPorNombre ?? "",
      ];
      const coincideBusqueda = !termino || camposBusqueda.some((valor) => valor.toLowerCase().includes(termino));
      if (!coincideBusqueda) return false;
      if (filtroEstado && receta.estado !== filtroEstado) return false;
      if (filtroVigenteDesde && (!receta.vigenteDesde || receta.vigenteDesde < filtroVigenteDesde)) return false;
      if (filtroVigenteHasta && (!receta.vigenteHasta || receta.vigenteHasta > filtroVigenteHasta)) return false;
      if (nombreFiltro && (receta.nombre ?? "").trim().toLowerCase() !== nombreFiltro) return false;
      return true;
    });
  }, [
    busquedaReceta,
    filtroEstado,
    filtroNombreReceta,
    filtroVigenteDesde,
    filtroVigenteHasta,
    recetasOrdenadas,
  ]);
  const columnasParaExportar = [
    "ID",
    "Código",
    "Nombre",
    "Vigente desde",
    "Vigente hasta",
    "Creado",
    "Actualizado",
    "Estado",
  ];

  const formatearFechaUsuario = (fecha?: string | null, nombre?: string | null) =>
    fecha
      ? `${new Date(fecha).toLocaleString()}${nombre ? ` (${nombre})` : ''}`
      : "-";

  const descargarCsv = () => {
    if (recetasFiltradas.length === 0) return;
      const filas = recetasFiltradas.map((receta) =>
        [
          receta.id,
          receta.codigo,
          receta.nombre,
          receta.vigenteDesde ?? "-",
          receta.vigenteHasta ?? "-",
          formatearFechaUsuario(receta.creadoEn, receta.creadoPorNombre),
          formatearFechaUsuario(receta.actualizadoEn, receta.actualizadoPorNombre),
          receta.estado,
        ].join(";"),
      );
    const contenido = [columnasParaExportar.join(";"), ...filas].join("\n");
    const blob = new Blob([contenido], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement("a");
    enlace.href = url;
    enlace.download = "recetas.csv";
    enlace.click();
    URL.revokeObjectURL(url);
  };

const descargarPdf = () => {
    if (recetasFiltradas.length === 0) return;
    const doc = new jsPDF({ orientation: "landscape" });
    const margen = 14;
    doc.setFontSize(14);
    doc.text("Listado de recetas", margen, 16);
    doc.setFontSize(10);
    let y = 30;
      const encabezado = columnasParaExportar.join(" | ");
    doc.text(encabezado, margen, y);
    y += 8;
    recetasFiltradas.forEach((receta) => {
      if (y > doc.internal.pageSize.height - 20) {
        doc.addPage();
        y = 20;
      }
      const fila = [
        receta.id,
        receta.codigo,
        receta.nombre,
        receta.vigenteDesde ?? "-",
        receta.vigenteHasta ?? "-",
        formatearFechaUsuario(receta.creadoEn, receta.creadoPorNombre),
        formatearFechaUsuario(receta.actualizadoEn, receta.actualizadoPorNombre),
        receta.estado,
      ].join(" | ");
      doc.text(fila, margen, y);
      y += 7;
    });
    doc.save("recetas.pdf");
  };

  const [tabActiva, setTabActiva] = useState<"formulario" | "listado">("formulario");

  useEffect(() => {
    const resultado = vigenciaSchema.safeParse({
      vigenteDesde: vigenciaDesde || undefined,
      vigenteHasta: vigenciaHasta || undefined,
    });
    setVigenciaError(resultado.success ? null : resultado.error.issues[0]?.message ?? null);
  }, [vigenciaDesde, vigenciaHasta]);

  useEffect(() => {
    const valor = Number(cantidadResultado);
    if (!cantidadResultado.trim()) {
      setCantidadError("Ingresá una cantidad.");
    } else if (!Number.isFinite(valor) || valor <= 0) {
      setCantidadError("Debe ser un número mayor que cero.");
    } else {
      setCantidadError(null);
    }
  }, [cantidadResultado]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Recetas de producción"
          pageTitle="Fábrica"
          pageLink="/produccion/recetas"
          description="Crea y sigue recetas multinivel para órdenes de producción."
        />
        <Nav tabs className="nav-tabs nav-tabs-custom mb-3">
          {[
            { id: "formulario", label: "Nueva receta" },
            { id: "listado", label: "Recetas registradas" },
          ].map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                className={tabActiva === tab.id ? "active" : ""}
                onClick={() => setTabActiva(tab.id as "formulario" | "listado")}
              >
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
        <TabContent activeTab={tabActiva}>
          <TabPane tabId="formulario">
            {!modalEdicionAbierto ? (
              <Card className="mb-4">
                <CardHeader className="border-0">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div>
                      <h5 className="mb-0">Recetas de producción</h5>
                      <p className="mb-0 text-muted">Crea y gestiona recetas multinivel.</p>
                    </div>
                    <Button
                      type="submit"
                      form="receta-form"
                      color="primary"
                      disabled={saving || !puedeEnviar}
                    >
                      Crear receta
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <RecetaFormulario
                    formId="receta-form"
                    onSubmit={handleSubmit}
                    selectedArticulo={selectedArticulo}
                    formatArticuloLabel={formatArticuloLabel}
                    abrirModal={abrirModal}
                    permitirSeleccionArticulo
                    vigenciaDesde={vigenciaDesde}
                    setVigenciaDesde={setVigenciaDesde}
                    vigenciaHasta={vigenciaHasta}
                    setVigenciaHasta={setVigenciaHasta}
                    vigenciaError={vigenciaError}
                    cantidadResultado={cantidadResultado}
                    setCantidadResultado={setCantidadResultado}
                    cantidadError={cantidadError}
                    items={items}
                    seleccionarArticulo={seleccionarArticulo}
                    handleItemChange={handleItemChange}
                    removerItem={removerItem}
                    agregarItem={agregarItem}
                    articulos={articulos}
                    totalCantidad={totalCantidad}
                    error={error}
                    datalistId={ARTICULOS_DATALIST_ID}
                  />
                </CardBody>
              </Card>
            ) : (
              <Card className="mb-4 border border-dashed">
                <CardBody>
                  <p className="mb-0 text-muted">
                    Estás editando la receta {recetaEditandoNombre ? `"${recetaEditandoNombre}"` : ""} en el modal.
                  </p>
                </CardBody>
              </Card>
            )}
          </TabPane>
          <TabPane tabId="listado">
            <Card>
              <CardHeader>
                <h5 className="mb-0">Listado de recetas</h5>
              </CardHeader>
              <CardBody>
                <Row className="align-items-center mb-3 g-2">
                  <Col lg={7}>
                    <InputGroup>
                      <Input
                        placeholder="Buscar por ID, código, nombre o estado..."
                        value={busquedaReceta}
                        onChange={(event) => setBusquedaReceta(event.target.value)}
                      />
                      <Button
                        color="soft-primary"
                        type="button"
                        className="text-nowrap"
                        onClick={() => setBusquedaReceta("")}
                      >
                        Limpiar
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col lg={5} className="d-flex justify-content-lg-end flex-wrap gap-2">
                    <Button className="btn-soft-success d-flex align-items-center" onClick={descargarCsv}>
                      <i className="ri-file-excel-2-line align-middle me-1" />
                      Descargar Excel
                    </Button>
                    <Button className="btn-soft-danger d-flex align-items-center" onClick={descargarPdf}>
                      <i className="ri-file-pdf-line align-middle me-1" />
                      Descargar PDF
                    </Button>
                    <Button color="secondary" outline onClick={abrirModalImportacion} className="d-flex align-items-center">
                      <i className="ri-upload-2-line me-1" />
                      Importar recetas
                    </Button>
                    <Button color="primary" outline onClick={() => setFiltrosVisibles(true)} className="d-flex align-items-center">
                      <i className="ri-filter-3-line me-1" />
                      Filtros
                    </Button>
                  </Col>
                </Row>
                {loading ? (
                  <p>Cargando...</p>
                ) : recetasFiltradas.length === 0 ? (
                  <p className="text-muted">No hay recetas disponibles.</p>
                ) : (
                  <RecetasListadoTabla
                    recetas={recetasFiltradas}
                    accionesReceta={accionesReceta}
                    esSwitch={esSwitch}
                    mostrarAccionPendiente={mostrarAccionPendiente}
                    onEditarReceta={iniciarEdicionReceta}
                    onVerReceta={abrirModalVerReceta}
                    onVerHistorial={abrirModalHistorial}
                    onToggleActivo={handleToggleActivo}
                    onAnularReceta={handleAnularReceta}
                    accionProcesando={accionProcesando}
                    estadoBadgeColor={estadoBadgeColor}
                    formatearFechaUsuario={formatearFechaUsuario}
                  />
                )}
              </CardBody>
            </Card>
          </TabPane>
        </TabContent>
      </Container>
      <Modal isOpen={modalImportacionAbierto} toggle={cerrarModalImportacion} size="md">
        <Form onSubmit={handleImportarRecetas}>
          <ModalHeader toggle={cerrarModalImportacion}>Importar recetas</ModalHeader>
          <ModalBody>
            <p className="text-muted">
              Cargá un archivo CSV o XLSX con la estructura de la plantilla para importar múltiples recetas en bloque.
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button type="button" color="link" className="p-0" onClick={descargarPlantillaImportacion}>
                <i className="ri-download-2-line me-1" /> Descargar plantilla de importación
              </Button>
            </div>
            <div className="mb-3">
              <Label className="form-label">Archivo</Label>
              <Input
                type="file"
                accept=".csv,.xlsx"
                onChange={manejarArchivoImportacion}
              />
              <FormText className="text-muted">
                La plantilla contiene los campos obligatorios y un ejemplo para ayudarte a completar los datos.
              </FormText>
            </div>
            {importError && <p className="text-danger mb-0">{importError}</p>}
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={cerrarModalImportacion} disabled={importando}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={importando || !archivoImportacion}>
              {importando ? "Importando..." : "Importar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Modal isOpen={modalEdicionAbierto} toggle={cerrarModalEdicion} size="xl" backdrop="static">
        <ModalHeader toggle={cerrarModalEdicion}>
          Editar receta {recetaEditandoNombre ? `- ${recetaEditandoNombre}` : ""}
        </ModalHeader>
        <ModalBody>
          {cargandoDetalleEdicion ? (
            <div className="py-4 text-center text-muted">Cargando receta...</div>
          ) : (
            <RecetaFormulario
              formId="receta-edit-form"
              onSubmit={handleSubmitEdicion}
              selectedArticulo={selectedArticulo}
              formatArticuloLabel={formatArticuloLabel}
              abrirModal={abrirModal}
              permitirSeleccionArticulo={false}
              vigenciaDesde={vigenciaDesde}
              setVigenciaDesde={setVigenciaDesde}
              vigenciaHasta={vigenciaHasta}
              setVigenciaHasta={setVigenciaHasta}
              vigenciaError={vigenciaError}
              cantidadResultado={cantidadResultado}
              setCantidadResultado={setCantidadResultado}
              cantidadError={cantidadError}
              items={items}
              seleccionarArticulo={seleccionarArticulo}
              handleItemChange={handleItemChange}
              removerItem={removerItem}
              agregarItem={agregarItem}
              articulos={articulos}
              totalCantidad={totalCantidad}
              error={error}
              datalistId={ARTICULOS_DATALIST_ID}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={cerrarModalEdicion} disabled={savingEdicion}>
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            form="receta-edit-form"
            disabled={savingEdicion || !puedeEnviar || cargandoDetalleEdicion}
          >
            Guardar cambios
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={modalVerRecetaAbierto} toggle={cerrarModalVerReceta} size="lg">
        <ModalHeader toggle={cerrarModalVerReceta}>
          Ver receta {detalleRecetaVista ? `- ${detalleRecetaVista.nombre}` : ""}
        </ModalHeader>
        <ModalBody>
          {cargandoDetalleVista ? (
            <div className="py-4 text-center text-muted">Cargando receta...</div>
          ) : detalleRecetaVista ? (
            <RecetaDetalleVista
              detalle={detalleRecetaVista}
              articulos={articulos}
              formatearFechaUsuario={formatearFechaUsuario}
            />
          ) : (
            <p className="text-muted mb-0">No se encontró la receta seleccionada.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={cerrarModalVerReceta}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={modalHistorialAbierto} toggle={cerrarModalHistorial} size="lg">
        <ModalHeader toggle={cerrarModalHistorial}>
          Historial de receta {recetaHistorialBase ? `- código ${recetaHistorialBase.codigo}` : ""}
        </ModalHeader>
        <ModalBody>
          {historialRecetas.length === 0 ? (
            <p className="text-muted mb-0">No se encontraron versiones para esta receta.</p>
          ) : (
            <RecetaHistorialTabla
              recetas={historialRecetas}
              formatearFechaUsuario={formatearFechaUsuario}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={cerrarModalHistorial}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={modalAbierto} toggle={cerrarModal} size="lg">
        <ModalHeader toggle={cerrarModal}>Seleccionar artículo receta</ModalHeader>
        <ModalBody>
          <InputGroup className="mb-3">
            <Input
              placeholder="Buscar artículo por código, SKU o descripción..."
              value={articuloBusqueda}
              onChange={(event) => setArticuloBusqueda(event.target.value)}
            />
            <Button color="soft-primary" type="button">
              Buscar
            </Button>
          </InputGroup>
          <div
            style={{
              maxHeight: MODAL_TABLE_MAX_HEIGHT,
              overflowY: "auto",
            }}
          >
            <Table bordered hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={STICKY_TH_STYLE}>ID</th>
                  <th style={STICKY_TH_STYLE}>SKU</th>
                  <th style={STICKY_TH_STYLE}>Descripción</th>
                  <th style={STICKY_TH_STYLE}>Unidad ID</th>
                  <th style={STICKY_TH_STYLE}>Es receta</th>
                  <th style={STICKY_TH_STYLE}>Compra</th>
                  <th style={STICKY_TH_STYLE}>Venta</th>
                  <th style={STICKY_TH_STYLE}></th>
                </tr>
              </thead>
              <tbody>
                {articulosFiltrados.map((articulo) => (
                  <tr key={`modal-articulo-${articulo.id}`}>
                    <td>{articulo.id}</td>
                    <td>{articulo.sku ?? "-"}</td>
                    <td>{articulo.descripcion}</td>
                    <td>{articulo.unidad_medida_id ?? "-"}</td>
                    <td>
                      <Badge color={articulo.es_receta ? "success" : "secondary"} pill>
                        {articulo.es_receta ? "Sí" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={articulo.puede_comprar ? "success" : "secondary"} pill>
                        {articulo.puede_comprar ? "Sí" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={articulo.puede_vender ? "success" : "secondary"} pill>
                        {articulo.puede_vender ? "Sí" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Button color="primary" size="sm" onClick={() => handleSeleccionDesdeModal(articulo)}>
                        Seleccionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
      </Modal>
      <Offcanvas direction="end" scrollable isOpen={filtrosVisibles} toggle={() => setFiltrosVisibles(false)}>
        <OffcanvasHeader toggle={() => setFiltrosVisibles(false)}>Filtros del listado</OffcanvasHeader>
        <OffcanvasBody>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label className="form-label">Estado</Label>
              <Input type="select" value={filtroEstado} onChange={(event) => setFiltroEstado(event.target.value)}>
                <option value="">Todos</option>
                <option value="activo">Activos</option>
                <option value="inactivo">Inactivos</option>
                <option value="anulado">Anulados</option>
              </Input>
            </div>
            <div>
              <Label className="form-label">Vigente desde</Label>
              <Input type="date" value={filtroVigenteDesde} onChange={(event) => setFiltroVigenteDesde(event.target.value)} />
            </div>
            <div>
              <Label className="form-label">Vigente hasta</Label>
              <Input type="date" value={filtroVigenteHasta} onChange={(event) => setFiltroVigenteHasta(event.target.value)} />
            </div>
            <div>
              <Label className="form-label">Nombre de la receta</Label>
              <Input
                list="recetas-nombres"
                value={filtroNombreReceta}
                onChange={(event) => setFiltroNombreReceta(event.target.value)}
                placeholder="Escribí o seleccioná un nombre"
              />
              <datalist id="recetas-nombres">
                {nombresRecetasDisponibles.map((nombre) => (
                  <option key={`nombre-receta-${nombre}`} value={nombre} />
                ))}
              </datalist>
            </div>
          </div>
          <div className="d-flex gap-2 mt-4">
            <Button color="primary" onClick={() => setFiltrosVisibles(false)}>
              Aplicar filtros
            </Button>
            <Button
              color="light"
              type="button"
              onClick={() => {
                setFiltroEstado("");
                setFiltroVigenteDesde("");
                setFiltroVigenteHasta("");
                setFiltroNombreReceta("");
              }}
            >
              Limpiar
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
      <Modal isOpen={Boolean(confirmacionEstado)} toggle={() => setConfirmacionEstado(null)}>
        {confirmacionEstado && (
          <>
            <ModalHeader toggle={() => setConfirmacionEstado(null)}>
              Confirmar cambio de estado
            </ModalHeader>
            <ModalBody>
              <p className="mb-2">
                <strong>{confirmacionEstado.receta.nombre}</strong> ({confirmacionEstado.receta.codigo})
              </p>
              <p className="text-muted mb-0">{confirmacionEstado.descripcion}</p>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => setConfirmacionEstado(null)}>
                Cancelar
              </Button>
              <Button color="primary" onClick={confirmarCambioEstado} disabled={Boolean(accionProcesando)}>
                Confirmar
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
      <Modal isOpen={Boolean(modalInfo)} toggle={cerrarModalResultado}>
        {modalInfo && (
          <>
            <ModalHeader toggle={cerrarModalResultado}>{modalInfo.titulo}</ModalHeader>
            <ModalBody>
              <p
                className={
                  modalInfo.tipo === "success"
                    ? "text-success"
                    : modalInfo.tipo === "error"
                    ? "text-danger"
                    : "text-info"
                }
              >
                {modalInfo.mensaje}
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={cerrarModalResultado}>
                Volver al formulario
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
};

const descargarPlantillaImportacion = () => {
  const encabezado = [
    "codigo_receta",
    "nombre_receta",
    "vigente_desde (AAAA-MM-DD)",
    "vigente_hasta (AAAA-MM-DD)",
    "articulo_id",
    "cantidad",
    "unidad",
  ];
  const ejemplo = [
    "REC-001",
    "Producto terminado ejemplo",
    "2025-01-01",
    "2025-12-31",
    "1001",
    "1.00",
    "kg",
  ];
  const contenido = [encabezado.join(";"), ejemplo.join(";")].join("\n");
  const blob = new Blob([contenido], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = "plantilla-importacion-recetas.csv";
  enlace.click();
  URL.revokeObjectURL(url);
};
export default RecetasPage;

type RecetaFormularioProps = {
  formId: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  selectedArticulo: StockArticulo | null;
  formatArticuloLabel: (articulo: StockArticulo) => string;
  abrirModal: (index: number | null) => void;
  permitirSeleccionArticulo: boolean;
  vigenciaDesde: string;
  setVigenciaDesde: (valor: string) => void;
  vigenciaHasta: string;
  setVigenciaHasta: (valor: string) => void;
  vigenciaError: string | null;
  cantidadResultado: string;
  setCantidadResultado: (valor: string) => void;
  cantidadError: string | null;
  items: RecetaItemForm[];
  seleccionarArticulo: (index: number, valor: string) => void;
  handleItemChange: (index: number, field: keyof RecetaItemForm, valor: string) => void;
  removerItem: (index: number) => void;
  agregarItem: () => void;
  articulos: StockArticulo[];
  totalCantidad: number;
  error: string | null;
  datalistId: string;
};

const RecetaFormulario: React.FC<RecetaFormularioProps> = ({
  formId,
  onSubmit,
  selectedArticulo,
  formatArticuloLabel,
  abrirModal,
  permitirSeleccionArticulo,
  vigenciaDesde,
  setVigenciaDesde,
  vigenciaHasta,
  setVigenciaHasta,
  vigenciaError,
  cantidadResultado,
  setCantidadResultado,
  cantidadError,
  items,
  seleccionarArticulo,
  handleItemChange,
  removerItem,
  agregarItem,
  articulos,
  totalCantidad,
  error,
  datalistId,
}) => (
  <Form id={formId} onSubmit={onSubmit}>
    <Row className="gy-3 mb-3">
      <Col md={6}>
        <Label className="form-label">Receta (artículo)</Label>
        <InputGroup>
          <Input
            value={selectedArticulo ? formatArticuloLabel(selectedArticulo) : "Sin seleccionar"}
            readOnly
          />
          <Button
            color="soft-primary"
            type="button"
            className="text-nowrap"
            onClick={() => abrirModal(null)}
            disabled={!permitirSeleccionArticulo}
            title={
              permitirSeleccionArticulo
                ? undefined
                : "No es posible cambiar la receta principal mientras estás editando."
            }
          >
            Buscar
          </Button>
        </InputGroup>
        <FormText className="text-muted">
          Elegí un artículo marcado como receta para crear la receta multinivel.
        </FormText>
      </Col>
      <Col md={2}>
        <Label className="form-label">Vigente desde</Label>
        <Input type="date" value={vigenciaDesde} onChange={(event) => setVigenciaDesde(event.target.value)} />
        {vigenciaError && (
          <FormText color="danger" className="mb-0">
            {vigenciaError}
          </FormText>
        )}
      </Col>
      <Col md={2}>
        <Label className="form-label">Vigente hasta</Label>
        <Input type="date" value={vigenciaHasta} onChange={(event) => setVigenciaHasta(event.target.value)} />
        {vigenciaError && (
          <FormText color="danger" className="mb-0">
            {vigenciaError}
          </FormText>
        )}
      </Col>
      <Col md={2}>
        <Label className="form-label">Cantidad</Label>
        <Input
          type="number"
          step="0.01"
          min="0.01"
          value={cantidadResultado}
          onChange={(event) => setCantidadResultado(event.target.value)}
        />
        {cantidadError && (
          <FormText color="danger" className="mb-0">
            {cantidadError}
          </FormText>
        )}
      </Col>
    </Row>
    <div className="mt-3">
      <h6>Ingredientes</h6>
      <Table responsive bordered className="mt-2">
        <thead className="table-light">
          <tr>
            <th>Código / descripción</th>
            <th>Unidad</th>
            <th>Es receta</th>
            <th>Cantidad</th>
            <th style={{ minWidth: 140 }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={`ingrediente-${index}`}>
              <td>
                <InputGroup>
                  <Input
                    list={datalistId}
                    value={item.articuloLabel ?? item.articuloId}
                    onChange={(event) => seleccionarArticulo(index, event.target.value)}
                    placeholder="ID - Artículo - Descripción (unidad)"
                  />
                  <Button
                    color="soft-primary"
                    size="sm"
                    type="button"
                    onClick={() => abrirModal(index)}
                  >
                    Buscar
                  </Button>
                </InputGroup>
              </td>
              <td>
                <Input value={item.unidad ?? ""} readOnly placeholder="Unidad" />
              </td>
              <td>
                <Badge
                  color={
                    item.esReceta === undefined
                      ? "secondary"
                      : item.esReceta
                      ? "success"
                      : "secondary"
                  }
                  pill
                >
                  {item.esReceta === undefined ? "—" : item.esReceta ? "Sí" : "No"}
                </Badge>
              </td>
              <td>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={item.cantidad}
                  onChange={(event) => handleItemChange(index, "cantidad", event.target.value)}
                />
              </td>
              <td className="d-flex gap-2">
                <Button
                  size="sm"
                  color="soft-danger"
                  disabled={items.length <= 1}
                  onClick={() => removerItem(index)}
                >
                  <i className="ri-delete-bin-5-line" />
                </Button>
                {index === items.length - 1 && (
                  <Button size="sm" color="soft-primary" onClick={agregarItem}>
                    <i className="ri-add-line align-bottom me-1" />
                    Agregar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <datalist id={datalistId}>
        {articulos.map((articulo) => (
          <option
            key={`articulo-option-${articulo.id}`}
            value={formatArticuloLabel(articulo)}
          />
        ))}
      </datalist>
      <div className="text-end text-muted">
        Total cantidad: {totalCantidad.toFixed(2)}
      </div>
    </div>
    {error && <p className="text-danger mt-2">{error}</p>}
  </Form>
);

type RecetaDetalleVistaProps = {
  detalle: RecetaDetalle;
  articulos: StockArticulo[];
  formatearFechaUsuario: (fecha?: string | null, nombre?: string | null) => string;
};

const RecetaDetalleVista: React.FC<RecetaDetalleVistaProps> = ({ detalle, articulos, formatearFechaUsuario }) => {
  const obtenerArticulo = (articuloId?: number | null) =>
    articuloId ? articulos.find((articulo) => articulo.id === articuloId) ?? null : null;
  const formatearArticulo = (articuloId?: number | null) => {
    if (!articuloId) return "-";
    const articulo = obtenerArticulo(articuloId);
    return articulo ? `${articulo.id} - ${articulo.descripcion}` : `ID ${articuloId}`;
  };
  return (
    <div className="d-flex flex-column gap-3">
      <Row className="gy-3">
        <Col md={6}>
          <div className="border rounded p-3 h-100">
            <p className="text-muted text-uppercase small mb-1">Receta</p>
            <h5 className="mb-1">{detalle.nombre}</h5>
            <p className="mb-0 text-muted">Código: {detalle.codigo}</p>
            <p className="mb-0 text-muted">Estado: {detalle.estado}</p>
          </div>
        </Col>
        <Col md={6}>
          <div className="border rounded p-3 h-100">
            <p className="text-muted text-uppercase small mb-1">Vigencia</p>
            <p className="mb-1">Desde: {detalle.vigenteDesde ?? "-"}</p>
            <p className="mb-1">Hasta: {detalle.vigenteHasta ?? "-"}</p>
            <p className="mb-1">Creado: {formatearFechaUsuario(detalle.creadoEn, detalle.creadoPorNombre)}</p>
            <p className="mb-0">
              Actualizado: {formatearFechaUsuario(detalle.actualizadoEn, detalle.actualizadoPorNombre)}
            </p>
          </div>
        </Col>
      </Row>
      {detalle.descripcion && (
        <div className="border rounded p-3">
          <p className="text-muted text-uppercase small mb-1">Descripción</p>
          <p className="mb-0">{detalle.descripcion}</p>
        </div>
      )}
      <div className="border rounded p-3">
        <h6 className="mb-3">Ingredientes</h6>
        <Table responsive striped bordered className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Artículo</th>
              <th>Tipo</th>
              <th>Cantidad</th>
              <th>Unidad</th>
            </tr>
          </thead>
          <tbody>
            {detalle.items && detalle.items.length > 0 ? (
              detalle.items.map((item, index) => (
                <tr key={`detalle-item-${index}`}>
                  <td>{formatearArticulo(item.articuloId ?? item.subrecetaId)}</td>
                  <td>{item.tipoItem ?? "materia_prima"}</td>
                  <td>{item.cantidad ?? "-"}</td>
                  <td>{item.unidadMedidaId ?? "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  La receta no tiene ingredientes registrados.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

type RecetaHistorialTablaProps = {
  recetas: RecetaResumen[];
  formatearFechaUsuario: (fecha?: string | null, nombre?: string | null) => string;
};

const RecetaHistorialTabla: React.FC<RecetaHistorialTablaProps> = ({ recetas, formatearFechaUsuario }) => (
  <Table responsive striped bordered className="mb-0">
    <thead className="table-light">
      <tr>
        <th>ID</th>
        <th>Nombre</th>
        <th>Vigente desde</th>
        <th>Vigente hasta</th>
        <th>Estado</th>
        <th>Creado</th>
        <th>Actualizado</th>
      </tr>
    </thead>
    <tbody>
      {recetas.map((receta) => (
        <tr key={`historial-${receta.id}`}>
          <td>{receta.id}</td>
          <td>{receta.nombre}</td>
          <td>{receta.vigenteDesde ?? "-"}</td>
          <td>{receta.vigenteHasta ?? "-"}</td>
          <td>
            <span className={`badge bg-${receta.estado === "activo" ? "success" : receta.estado === "inactivo" ? "warning" : "danger"}-subtle text-${receta.estado === "activo" ? "success" : receta.estado === "inactivo" ? "warning" : "danger"}`}>
              {receta.estado}
            </span>
          </td>
          <td>{formatearFechaUsuario(receta.creadoEn, receta.creadoPorNombre)}</td>
          <td>{formatearFechaUsuario(receta.actualizadoEn, receta.actualizadoPorNombre)}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);
